import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import dayjs from 'dayjs';

import { useFetch } from 'src/lib/FetchContext';
import { usePrefetchSuspense } from 'src/lib/usePrefetch';
import { interestLevels } from 'src/lib/interestLevels';
import { itemClassMap } from 'src/lib/itemClassMap';
import { sanitizeWithDashes } from 'src/lib/catalog';

import { InputError, SocialSharingProvider } from '@riac/design-system';
import { Input } from '@riac/design-system';
import { Breadcrumb } from '@riac/design-system';

import {
  DetailPageContainer,
  DetailPageHeadlineSection,
  DetailPageMainSection,
  DetailPageOpenSeaDragonRatioWrapper,
  DetailPageVideoWrapper,
  DetailsPageDetailRichDescriptionHeader,
  DetailsPageRelatedItemsTitle, DetailsPageRelatedItems,
  DetailsPageRelatedItem,
  InputErrorContainer,
  GoToLotContainer,
  DetailPageEstimatedPrice,
  GoToLotInput,
  GoToLotIncrementer,
  GoToLotIncrementerLotLabel,
  DetailPageMobileHeadlineSection,
} from 'src/pages/DetailPage/DetailPage.style';
import { DetailPageThumbnailBar } from 'src/pages/DetailPage/components/DetailPageThumbnailBar';
import { ContentContainer } from '@riac/design-system';
import { MarkdownContentArea } from '@riac/design-system';
import { DetailPageActionArea } from 'src/pages/DetailPage/components/DetailPageActionArea';
import { DetailPageQuestionArea } from 'src/pages/DetailPage/components/DetailPageQuestionArea';
import { DetailPageOpenSeaDragon } from 'src/pages/DetailPage/components/DetailPageOpenSeaDragon';
import {
  PageTabsContainer, PageTabsHeader, PageTabsHeaderItem,
  PageTabsBody, PageDetailTabTable, PageDetailTabLine, PageDetailTabField,
} from '@riac/design-system';
import { CallToAction } from '@riac/design-system';

import { BoxButton } from '@riac/design-system';
import { Button } from '@riac/design-system';
import { TextButton } from '@riac/design-system';
import { SolidButton } from '@riac/design-system';

import { DetailPageContext } from 'src/pages/DetailPage/DetailPageContext';
import { CatalogHistoryContext } from 'src/lib/CatalogHistoryContext';
import { UserItemContextProvider } from 'src/lib/UserItemContext';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { WishlistActionContext } from 'src/lib/WishlistActionContext';
import { LoginContext } from 'src/lib/LoginContext';
import { ImgLazyLoader } from '@riac/design-system';
import { LotImageObject } from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItem.style';
import { ParsedHeadlineMess, parseLotHeadlineMess } from 'src/lib/auctionItemUtils';
import { BiddingContext, BiddingContextProvider } from 'src/lib/BiddingContext';
import { getIsPricelessLot } from 'src/lib/getIsPricelessLot';
import { SocketContext } from 'src/lib/SocketContext';
import { LoginToUse } from 'src/components/LoginToUse/LoginToUse';
import { cookError, loglog } from 'src/lib/loglog';

let timer = null;

export const DetailPage = (): JSX.Element => {
  const { fetch } = useFetch();
  const history = useHistory();
  const params = useParams<{ auctionid: string, lotnumber: string, slug: string }>();
  const { latestCatalogPage } = useContext(CatalogHistoryContext);

  const auctionId = parseInt(params.auctionid);
  const lotNumber = parseInt(params.lotnumber);
  const [initLoad, setInitLoad] = useState<boolean>(true);
  const [lotInput, setLotInput] = useState<string>('');
  // combine photo index states because they can depend on each other's state when updating
  const [activeIndices, setActiveIndices] = useState({ photo: 0, subPhoto: 0 });
  // destructure individual values and setters
  const activePhotoIndex = activeIndices.photo;
  const activeSubPhotoIndex = activeIndices.subPhoto;
  const setActivePhotoIndex = (value): void => setActiveIndices((prev) => ({ ...prev, photo: value }));
  const setActiveSubPhotoIndex = (value): void => setActiveIndices((prev) => ({ ...prev, subPhoto: value }));
  const [timedAuctionOverrideInfo, setTimedAuctionOverrideInfo] = useState(undefined);

  const [activeVideoIndex, setActiveVideoIndex] = useState(-1);
  const [tab, setTab] = useState(0);

  const wishlistActions = useContext(WishlistActionContext);
  const { isLoggedIn } = useContext(LoginContext);
  const { socket, setOnOnTimePage } = useContext(SocketContext);

  useEffect(() => {
    if(isLoggedIn) {
      wishlistActions.setAuction(auctionId);
    }
  }, [auctionId]);

  const itemInfoDefaults = {
    Photos: [],
    timestamp: null,
  };

  const refetchData = async (auctionId, lotNumber) => {
    const data = await (await fetch(`/api/detail/${auctionId}/${lotNumber}`)).json();
    data.timestamp = Date.now();
    setItemInfo(Object.assign({}, itemInfoDefaults, data));
  };

  const onLotChange = async () => {
    try{
      if(initLoad) {
        setInitLoad(false);
      } else {
        await fetch(`/api/detail/view/${auctionId}/${lotNumber}`);
      }
      refetchData(auctionId, lotNumber).then();
      setActiveIndices({ photo: 0, subPhoto: 0 });
      setLotInput('');
    } catch (err) {
      loglog.error('Lot Change Update', {err: cookError(err)});
    }
  };

  const prefetchedItemInfo = usePrefetchSuspense('detail-page', async () => {
    await fetch(`/api/detail/view/${auctionId}/${lotNumber}`);
    const detailResponse = await fetch(`/api/detail/${auctionId}/${lotNumber}`);
    const detailResults = await detailResponse.json();
    detailResults.timestamp = Date.now();
    return detailResults;
  }).read();
  const [itemInfo, setItemInfo] = useState(Object.assign({}, itemInfoDefaults, prefetchedItemInfo));
  let primaryItem = itemInfo?.Items[0] || null;

  useEffect(() => {
    primaryItem = itemInfo?.Items[0] || null;
  }, [itemInfo.timestamp, auctionId, lotNumber]);

  useEffect(() => {
    onLotChange();
  }, [auctionId, lotNumber]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, []);

  // parse headlines
  const parsedHeadlines = primaryItem && itemInfo.Items.map<ParsedHeadlineMess>((item) => parseLotHeadlineMess(item.Headline, item.LotExt));
  // STUB: If this resolves to null the whole page breaks
  // not sure if that's by design or not
  const primaryParsedHeadline = primaryItem && parsedHeadlines?.[0]?.lotHeadline;

  useEffect(() => {
    if(socket && itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      socket.emit('join-room', `timed-auction-${auctionId}`);
      setOnOnTimePage(true);
    } else {
      setOnOnTimePage(false);
    }
    return () => {
      if(socket && itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        socket.emit('leave-room', `timed-auction-${auctionId}`);
        setOnOnTimePage(false);
      }
    };
  }, [socket?.id, itemInfo?.AuctionTypeID]);

  const updateTimedItemInfo = (item, currentBid, endDate, highBidder, highBidAmount) => {
    const newItem = { ...item };
    newItem.CurrentBid = currentBid;
    newItem.EndDate = endDate;

    // If we are the high bidder and the current bid is over our high bid, we are no longer the high bid
    if(newItem.HighBidder && currentBid > newItem.HighBidAmount) {
      newItem.HighBidder = false;
      newItem.PreviousBid = true;
      newItem.HighBidAmount = null;
    }
    // if we are now the high bidder
    if(typeof highBidder !== 'undefined' && typeof highBidAmount !== 'undefined') {
      newItem.HighBidAmount = highBidAmount;
      newItem.HighBidder = highBidder;
      if(!highBidder) {
        newItem.PreviousBid = 1;
        newItem.HighBidAmoun = null;
      }
    }
    return newItem;
  };

  useEffect(() => {
    const lotUpdateHandler = (
      auctionItemID: number | string,
      currentBid: number,
      endDate: string,
      highBidder?: boolean,
      highBidAmount?: number | null,
    ): void => {
      if(String(itemInfo?.Items[0]?.AuctionItemID) !== String(auctionItemID)) {
        return;
      }

      addTimer(auctionId, lotNumber, endDate);
      setItemInfo((oldItem) => ({
        ...updateTimedItemInfo(oldItem, currentBid, endDate, highBidder, highBidAmount),
        Items: oldItem.Items.map((oi, i) => {
          if(i === 0) {
            return updateTimedItemInfo(oi, currentBid, endDate, highBidder, highBidAmount);
          }
          return oi;
        }),
      }));
    };
    if(socket && itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      socket.on('lot update', lotUpdateHandler);
    }
    return () => {
      if(socket && itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        socket.off('lot update', lotUpdateHandler);
      }
    };
  }, [itemInfo.timestamp, auctionId, lotNumber]);

  const addTimer = (aId, lotNumber, endDate) => {
    if(timer) {
      clearTimeout(timer);
      timer = null;
    }
    const now = dayjs();
    const end = dayjs(endDate);
    const diff = end.diff(now);
    if(diff > 0 && diff < 86400000) {
      timer = setTimeout(async () => {
        await updateTimedInfo(aId, lotNumber);
      }, diff);
    }
  };

  const clearTimer = () => {
    if(timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const updateTimedInfo = async (aId, lot) => {
    const timedInfo = await (await fetch('/api/timed-auction/refresh-lot-data', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        {
          auctionId: aId,
          lotNumbers: lot,
        }
      ),
    })).json();
    if(timedInfo && timedInfo.success && timedInfo.data[lot]) {
      addTimer(auctionId, lotNumber, timedInfo.data[lot].endDate);
      setItemInfo((oldItem) => ({
        ...updateTimedItemInfo(oldItem, timedInfo.data[lot].currentBid, timedInfo.data[lot].endDate, timedInfo.data[lot].highBidder, timedInfo.data[lot].highBidAmount),
        Items: oldItem.Items.map((oi, i) => {
          if(i === 0) {
            return updateTimedItemInfo(oi, timedInfo.data[lot].currentBid, timedInfo.data[lot].endDate, timedInfo.data[lot].highBidder, timedInfo.data[lot].highBidAmount);
          }
          return oi;
        }),
      }));
    }
  };

  useEffect(() => {
    const visChangeHandler = async (event) => {
      if(!document.hidden) {
        await updateTimedInfo(auctionId, lotNumber);
      }
    };

    const reconHandler = async () => {
      await updateTimedInfo(auctionId, lotNumber);
    };
    if(itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      addTimer(auctionId, lotNumber, itemInfo.EndDate);
      window.addEventListener('visibilitychange', visChangeHandler);
      socket.on('reconnect', reconHandler);
      socket.on('connect', reconHandler);
    }

    return () => {
      if(itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        clearTimer();
        window.removeEventListener('visibilitychange', visChangeHandler);
        socket.off('reconnect', reconHandler);
        socket.off('connect', reconHandler);
      }
    };
  }, [itemInfo.timestamp, auctionId, lotNumber]);


  if(!primaryItem) {
    return (
      <React.Fragment>
        <InputError error={'An error occurred loading this item'}/>
        <InputErrorContainer>
          <h1>We're sorry - we can't find the lot you're looking for.</h1>
          <p>
            We might have removed the page when we redesigned our website, the link you clicked
            might be old and does not work anymore, or you might have accidentally typed the wrong
            URL in the address bar. To find other items in this auction, you can visit our{' '}
            <TextButton to={latestCatalogPage || `/catalog/${auctionId}`}>catalog page</TextButton>{' '}
            or you can use the form below to view a different lot number.
          </p>
          <Input
            type="text"
            aria-label="Go To Lot"
            value={lotInput}
            onChange={(val) => setLotInput(val)}
            onKeyDown={(e) => e.key === 'Enter' && lotInput.length > 0 && history.push(`/detail/${params.auctionid}/${lotInput}`)}
            after={
              <BoxButton onPress={() => lotInput.length > 0 && history.push(`/detail/${params.auctionid}/${lotInput}`)}>Go
                to Lot</BoxButton>
            }
          />
        </InputErrorContainer>
        <CallToAction
          title={'Didn\'t find what you were looking for?'}
          content={'Rock Island Auction Company is the global leader in firearms auctions, '
            + 'currently holding multiple records for our top-selling lots. View our latest '
            + 'catalog to find what you might be searching for.'}
          button={<SolidButton to="/catalog">View Catalog</SolidButton>}
        />
      </React.Fragment>
    );
  }

  const detailSlug = sanitizeWithDashes(primaryItem.WebTitle).toLowerCase();

  // Redirect to correct slug if it doesn't exist or is wrong.
  if(!params.slug || params.slug !== detailSlug) {
    return <Redirect
      to={`/detail/${params.auctionid}/${params.lotnumber}/${detailSlug}`}
    />;
  }

  const multiItemLot = itemInfo?.Items.length > 1;

  const videos = itemInfo?.Items.reduce((prev, cur) => {
    if(!cur.VideoLink) {
      return prev;
    }
    return [
      ...prev,
      ...cur.VideoLink.split(','),
    ];
  }, []);

  const AuctionStatusID = itemInfo?.AuctionStatusID;
  const SessionStatus = (itemInfo?.Items[0]?.AuctionSessionStatus || 0);
  const aPriceRealized = !!itemInfo?.PricesRealized;
  const AuctionTypeID = itemInfo?.AuctionTypeID;

  let allowBid = false;
  const now = dayjs();
  const completedLot = itemInfo && (
    dayjs(itemInfo.StartDate).isBefore(now) && dayjs(itemInfo.EndDate).add(24, 'hour').isAfter(now)
  ) && (
    itemInfo.activeLot > itemInfo.Items[0].LotNumber
  );
  if(AuctionStatusID === 2 && (((SessionStatus === 2 || SessionStatus === 3) && !aPriceRealized && !completedLot) || AuctionTypeID === TIMED_AUCTION_TYPE_ID)) {
    allowBid = true;
  }

  let hasNextPhoto = false;
  try {
    if(itemInfo?.Photos?.length > 0) {
      hasNextPhoto = !!(
        (activeSubPhotoIndex && itemInfo?.Photos[activePhotoIndex][activeSubPhotoIndex])
        || itemInfo?.Photos[activePhotoIndex + 1]
      );
    }
  } catch (e) {
    console.error('Error in checking hasNextPhoto', e);
  }
  let hasPrevPhoto = false;
  try {
    if(itemInfo?.Photos?.length > 0) {
      hasPrevPhoto = !!(
        (
          activeSubPhotoIndex
          && activeSubPhotoIndex !== 1
          && itemInfo?.Photos[activePhotoIndex][activeSubPhotoIndex - 1]
        )
        || itemInfo?.Photos[activePhotoIndex - 1]
      );
    }
  } catch (e) {
    console.error('Error in checking hasPrevPhoto', e);
  }

  function goToNextPhoto(): void {
    const photos = itemInfo.Photos;
    // is current photo part of a subgroup and not the last one
    // in that group? subphotoindex is 1-based when group exists
    if(activeSubPhotoIndex && photos[activePhotoIndex][activeSubPhotoIndex]) {
      setActiveSubPhotoIndex(activeSubPhotoIndex + 1);
      return;
    }
    // check next photo index exists
    if(photos[activePhotoIndex + 1]) {
      // single photo string or sub photo array determines subindex
      const newSub = Array.isArray(photos[activePhotoIndex + 1])
        ? 1 : 0;
      // move to next photo index
      setActiveIndices((prev) => ({ photo: prev.photo + 1, subPhoto: newSub }));
    }
  }

  function goToPrevPhoto(): void {
    const photos = itemInfo.Photos;
    // is current photo part of a subgroup and not the first one
    // in that group? subphotoindex is 1-based when group exists
    if(activeSubPhotoIndex && activeSubPhotoIndex !== 1 && photos[activePhotoIndex][activeSubPhotoIndex - 1]) {
      setActiveSubPhotoIndex(activeSubPhotoIndex - 1);
      return;
    }
    // check previous photo index exists
    if(photos[activePhotoIndex - 1]) {
      // single photo string or sub photo array determines subindex
      const newSub = Array.isArray(photos[activePhotoIndex - 1]) ? photos[activePhotoIndex - 1].length : 0;
      // move to previous photo index
      setActiveIndices((prev) => ({ photo: prev.photo - 1, subPhoto: newSub }));
    }
  }

  return (
    <SocialSharingProvider>
      <DetailPageContext.Provider value={{}}>
        <BiddingContextProvider
          auctionId={auctionId}
          auctionTypeId={itemInfo?.AuctionTypeID}
        >
          <DetailPageContainer>
            <Breadcrumb
              content={[
                { slug: `/catalog/${auctionId}`, text: `Auction Catalog #${auctionId}` },
                { slug: `/detail/${auctionId}/${lotNumber}`, text: `Lot #${lotNumber}` },
              ]}
            />
            <GoToLotContainer>
              <GoToLotIncrementer>
                {itemInfo?.PrevLot
                  && <>
                    <GoToLotIncrementerLotLabel>
                      Lot #{itemInfo.PrevLot}
                    </GoToLotIncrementerLotLabel>
                    <TextButton to={`/detail/${auctionId}/${itemInfo.PrevLot}`}>
                      <FaChevronLeft size="2em"/>
                    </TextButton>
                  </>
                }
              </GoToLotIncrementer>
              <GoToLotInput
                type="text"
                aria-label="Go To Lot"
                value={lotInput}
                onChange={(val) => setLotInput(val)}
                onKeyDown={(e) => e.key === 'Enter' && lotInput.length > 0 && history.push(`/detail/${params.auctionid}/${lotInput}`)}
                after={
                  <BoxButton
                    onPress={() => lotInput.length > 0 && history.push(`/detail/${params.auctionid}/${lotInput}`)}>
                    Go to Lot
                  </BoxButton>
                }
              />
              <GoToLotIncrementer>
                {itemInfo?.NextLot
                  && <>
                    <TextButton to={`/detail/${auctionId}/${itemInfo.NextLot}`}>
                      <FaChevronRight size="2em"/>
                    </TextButton>
                    <GoToLotIncrementerLotLabel>
                      Lot #{itemInfo.NextLot}
                    </GoToLotIncrementerLotLabel>
                  </>
                }
              </GoToLotIncrementer>
            </GoToLotContainer>
            <UserItemContextProvider auctionId={auctionId}>
              <DetailPageMainSection>
                <DetailPageMobileHeadlineSection>
                  <h1>Lot {lotNumber}: {primaryItem?.WebTitle.replace(/\-\w\).+/g, '')}</h1>
                  {primaryParsedHeadline !== primaryItem?.WebTitle
                    && <h2>{primaryParsedHeadline}</h2>
                  }
                  {itemInfo?.AuctionTypeID !== TIMED_AUCTION_TYPE_ID
                    && <>
                      <h3>Auction Location: {itemInfo?.LocCity}, {itemInfo?.LocState}</h3>
                      <h3>Auction Date: {dayjs(primaryItem?.Date).format('MMMM D, YYYY')}</h3>
                    </>
                  }
                </DetailPageMobileHeadlineSection>
                <ContentContainer>
                  <div>
                    <DetailPageOpenSeaDragonRatioWrapper>
                      {activeVideoIndex !== -1
                        && <DetailPageVideoWrapper>
                          <iframe
                            title="YouTube Video"
                            className="embed-responsive-item"
                            src={videos[activeVideoIndex] + '?rel=0&enablejsapi=1'}
                          />
                        </DetailPageVideoWrapper>
                        || <DetailPageOpenSeaDragon
                          photo={
                            activeSubPhotoIndex > 0 ? (
                              itemInfo?.Photos[activePhotoIndex][activeSubPhotoIndex - 1]
                            ) : (
                              itemInfo?.Photos[activePhotoIndex]
                            )
                          }
                          NRAID={primaryItem?.NRAID}
                          auctionId={auctionId}
                          nextPhoto={hasNextPhoto && goToNextPhoto}
                          prevPhoto={hasPrevPhoto && goToPrevPhoto}
                        />
                      }

                    </DetailPageOpenSeaDragonRatioWrapper>
                    <DetailPageThumbnailBar
                      activePhotoIndex={activePhotoIndex}
                      setActivePhotoIndex={setActivePhotoIndex}
                      activeSubPhotoIndex={activeSubPhotoIndex}
                      setActiveSubPhotoIndex={setActiveSubPhotoIndex}
                      activeVideoIndex={activeVideoIndex}
                      setActiveVideoIndex={setActiveVideoIndex}
                      auctionId={auctionId}
                      lotExtensions={itemInfo?.Items?.map((item) => item.LotExt)}
                      photos={itemInfo?.Photos}
                      videos={videos}
                      NRAID={primaryItem?.NRAID}
                    />
                  </div>
                  <div>
                    <DetailPageHeadlineSection>
                      <h1>Lot {lotNumber}: {primaryItem?.WebTitle.replace(/\-\w\).+/g, '')}</h1>
                      {primaryParsedHeadline !== primaryItem?.WebTitle
                        && <h2>{primaryParsedHeadline}</h2>
                      }
                      {itemInfo?.AuctionTypeID !== TIMED_AUCTION_TYPE_ID
                        && <>
                          <h3>Auction Location: {itemInfo?.LocCity}, {itemInfo?.LocState}</h3>
                          <h3>Auction Date: {dayjs(primaryItem?.Date).format('MMMM D, YYYY')}</h3>
                        </>
                      }
                    </DetailPageHeadlineSection>
                    {allowBid
                      && <DetailPageActionArea
                        auctionId={auctionId}
                        itemInfo={itemInfo}
                        primaryItem={primaryItem}
                      />
                      || <>
                        {(itemInfo?.AuctionTypeID !== TIMED_AUCTION_TYPE_ID)
                          && <DetailPageEstimatedPrice>
                            Estimated Price: {
                              getIsPricelessLot(primaryItem?.AuctionID, primaryItem?.LotNumber) ? 'Priceless'
                                : (
                                  (primaryItem?.LowEst && primaryItem?.HighEst)
                                    ? `$${primaryItem?.LowEst.toLocaleString()} - $${primaryItem?.HighEst.toLocaleString()}`
                                    : 'Not Available'
                                )
                            }
                          </DetailPageEstimatedPrice>}
                        {(itemInfo?.PricesRealized && primaryItem?.PriceRealized)
                          && <DetailPageEstimatedPrice>
                            Price Realized:
                            <LoginToUse overlayText={'Login to view'} hideChildren alwaysShowOverlay
                              childrenWrapperClassName={'p-2'}>
                              <img src={primaryItem?.PriceRealized}/>
                            </LoginToUse>
                          </DetailPageEstimatedPrice>
                          || ''}
                      </>
                    }
                  </div>
                </ContentContainer>
              </DetailPageMainSection>
              <PageTabsContainer>
                <PageTabsHeader>
                  <PageTabsHeaderItem active={tab === 0} onPress={() => {
                    setTab(0);
                  }}
                  >
                    Details
                  </PageTabsHeaderItem>
                </PageTabsHeader>
                <PageTabsBody>
                  {tab === 0 && <>
                    {itemInfo?.Items.map((item, itemIndex) => (
                      <React.Fragment key={itemIndex}>
                        <h4>
                          {(() => {
                            const parsed = parsedHeadlines[itemIndex];

                            const ret = parsed.extension
                              ? `${item.LotExt}) ${parsed.extensionHeadline}`
                              : parsed.lotHeadline;
                            return ret;
                          })()}
                        </h4>
                        <PageDetailTabTable>
                          <div>
                            <PageDetailTabLine>
                              <PageDetailTabField>Manufacturer:</PageDetailTabField> {item.ManufacturerName}
                            </PageDetailTabLine>
                            <PageDetailTabLine>
                              <PageDetailTabField>Model:</PageDetailTabField> {item.ModelDescription}
                            </PageDetailTabLine>
                            <PageDetailTabLine>
                              <PageDetailTabField>Type:</PageDetailTabField> {item.GunType || item.OtherType}
                            </PageDetailTabLine>
                            <PageDetailTabLine>
                              <PageDetailTabField>Gauge:</PageDetailTabField> {item.Guage}
                            </PageDetailTabLine>
                            <PageDetailTabLine>
                              <PageDetailTabField>Barrel:</PageDetailTabField> {item.BBL}
                            </PageDetailTabLine>
                            <PageDetailTabLine>
                              <PageDetailTabField>Finish:</PageDetailTabField> {item.Finish}
                            </PageDetailTabLine>
                            <PageDetailTabLine>
                              <PageDetailTabField>Grip:</PageDetailTabField> {item.Grips}
                            </PageDetailTabLine>
                            <PageDetailTabLine>
                              <PageDetailTabField>Stock:</PageDetailTabField> {item.Stock}
                            </PageDetailTabLine>
                          </div>
                          <div>
                            <PageDetailTabLine>
                              <PageDetailTabField>Item Views:</PageDetailTabField> {item.ItemViews}
                            </PageDetailTabLine>
                            {item.ItemInterest > 0
                              && <PageDetailTabLine>
                                <PageDetailTabField>Item Interest:</PageDetailTabField>
                                {' '}
                                {interestLevels[item.ItemInterest > 3 ? 3 : item.ItemInterest].description}
                              </PageDetailTabLine>
                            }
                            {item.SerialNumber.length > 15
                              && <PageDetailTabLine>
                                <PageDetailTabField>Serial Number:</PageDetailTabField> <img src={item.SerialNumber}/>
                              </PageDetailTabLine>
                              || ''}
                            {item.CatPage
                              && <PageDetailTabLine>
                                <PageDetailTabField>Catalog Page:</PageDetailTabField> {item.CatPage}
                              </PageDetailTabLine>
                            }
                            <PageDetailTabLine>
                              <PageDetailTabField>Class:</PageDetailTabField> {itemClassMap[item.A_MCode]}
                            </PageDetailTabLine>
                          </div>
                        </PageDetailTabTable>
                        {item.BoreCondition
                          && <PageDetailTabLine>
                            <PageDetailTabField>Bore Condition:</PageDetailTabField> {item?.BoreCondition}
                          </PageDetailTabLine>
                        }
                        {item.ConditionRating
                          && <PageDetailTabLine>
                            <PageDetailTabField>Rating:</PageDetailTabField>
                            <br/>
                            {item?.ConditionRatingDescription?.length > 0
                              && <>
                                {item.ConditionRatingDescription.map((desc) => (
                                  <p key={desc}>
                                    {/* <br/> */}
                                    {desc}
                                  </p>
                                ))}
                              </>
                              || item.ConditionRating
                            }
                          </PageDetailTabLine>
                        }
                        {item.HTMLDescription
                          && <>
                            <DetailsPageDetailRichDescriptionHeader>
                              Description
                            </DetailsPageDetailRichDescriptionHeader>
                            <MarkdownContentArea
                              dangerouslySetInnerHTML={{
                                __html: `${item.HTMLDescription.replace(/color: black;/g, '')}`,
                              }}
                            />
                          </>
                          || <PageDetailTabLine>
                            <PageDetailTabField>Description:</PageDetailTabField>
                            <br/>
                            <p>{item.DetailDesc}</p>
                          </PageDetailTabLine>
                        }
                        {item.ConditionDesc
                          && <PageDetailTabLine>
                            <PageDetailTabField>Rating Definition:</PageDetailTabField>
                            <br/>
                            <p>{item.ConditionDesc}</p>
                            <br/><br/>
                          </PageDetailTabLine>
                        }
                      </React.Fragment>
                    ))}
                  </>}
                </PageTabsBody>
              </PageTabsContainer>
              <DetailPageQuestionArea itemInfo={itemInfo} allowAsk={allowBid}/>
              <ContentContainer>
                <DetailsPageRelatedItemsTitle>Related Items</DetailsPageRelatedItemsTitle>
                <DetailsPageRelatedItems>
                  {itemInfo?.relatedItems?.map((relatedItem) => (
                    <DetailsPageRelatedItem key={relatedItem.lotNumber + relatedItem.auctionId + relatedItem.webTitle}>
                      <Button to={`/detail/${relatedItem.auctionId}/${relatedItem.lotNumber}`}>
                        <ImgLazyLoader>
                          <LotImageObject
                            type="image/jpeg"
                            data={
                              `${
                                window.CDN_PATH
                              }${
                                relatedItem.auctionId
                              }/255X159/${
                                relatedItem.lotNumber
                              }.jpg?cachebust=${
                                Math.random()
                              }`
                            }
                          >
                            <img src={`${window.CDN_PATH}notfound_255X159.png`} alt={relatedItem.webTitle}/>
                          </LotImageObject>
                        </ImgLazyLoader>
                        <h4>Lot #{relatedItem.lotNumber}: {relatedItem.webTitle}</h4>
                      </Button>
                    </DetailsPageRelatedItem>
                  ))}
                </DetailsPageRelatedItems>
              </ContentContainer>
            </UserItemContextProvider>
          </DetailPageContainer>
        </BiddingContextProvider>
      </DetailPageContext.Provider>
    </SocialSharingProvider>
  );
};


export default DetailPage;
